import React from 'react'
import classNames from 'classnames'
import { slugs } from '@/constants/slugs'
import { Translate } from '@/utils/translate/translate-client'
import { getProjectStyle, createContactSupportAnswer } from '../shared'
import { GiveawayFaq } from '../types'

export const getYoungWashingtonFaqs = (): GiveawayFaq[] => [
  {
    key: 'releaseDate',
    question: (t) => t('YoungWashingtonDateQuestion', 'When will the movie Young Washington be in theaters?'),
    answer: (t) => (
      <span className={getProjectStyle(slugs.sketch)}>
        {t(
          'YoungWashingtonReleaseDateAnswerv2',
          'YOUNG WASHINGTON will open in theaters on the weekend of July 4th, 2026, the 250th anniversary of The United States of America.',
        )}
      </span>
    ),
  },
  {
    key: 'whyShouldIBuyNow',
    question: (t) => t('whyShouldIBuyNow', 'Why should I buy now?'),
    answer: (t) => (
      <span className={getProjectStyle(slugs.sketch)}>
        {t(
          'YoungWashingtonwhyShouldIBuyNowAnswer',
          'Buying now does more than reserve your seat—it sends a message. Early ticket sales help prove there’s demand for stories like Young Washington that are honest, nuanced, and rooted in history. The more people who buy early, the more theaters, and the more Americans we can reach. Plus, early buyers get access to exclusive perks like giveaways, behind-the-scenes content, and limited-edition merch. If you believe in this kind of storytelling—and in America—now’s the absolute best time to show it.',
        )}
      </span>
    ),
  },
  {
    key: 'whoIsTheDirector',
    question: (t) => t('whoisthedirector?', 'Who is the director?'),
    answer: (t) => (
      <span className={getProjectStyle(slugs.sketch)}>
        {t(
          'YoungWashingtonwhoisthedirectorAnswer',
          'Jon Erwin is an acclaimed, multi-GMA Dove Award-winning director, writer, producer and entrepreneur. He is the creative force behind HOUSE OF DAVID and serves as Chief Creative Officer for Wonder Project. He is also the first and only director to receive an A+ CinemaScore® four times. His 2023 film JESUS REVOLUTION grossed $52M worldwide and was a breakout hit for Lionsgate.',
        )}
      </span>
    ),
  },
  {
    key: 'isThisCrowdfunded',
    question: (t) => t('isThisCrowdfunded?', 'Is this being crowd funded?'),
    answer: (t) => (
      <span className={getProjectStyle(slugs.sketch)}>
        {t(
          'YoungWashingtonisThisCrowdfundedAnswer',
          'No, YOUNG WASHINGTON is already fully funded and going into production for a July 4th 2026 release. We’re making the movie, and your support by pre-purchasing tickets will send a message to theaters and other Americans that this is the movie patriots want to enjoy as we celebrate America’s 250th birthday next year.',
        )}
      </span>
    ),
  },
  {
    key: 'giveawayDates',
    question: (t) =>
      t('whenDoesGiveawayStartSketch', 'When does the Giveaway begin and end, and when are winners announced?'),
    answer: (t) => (
      <Translate t={t} i18nKey="whenDoesGiveawayStartYWAnswerV2">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            The Promotion begins at 12:00 a.m. Mountain Time (&quot;MT&quot;) on June 18, 2025 and ends at 11:59 p.m. MT
            on August 5, 2025.
          </li>
          <li>
            <strong>The Grand Prize Drawing will take place on or about August 19, 2025.</strong>
          </li>
          <li>
            Visit the <strong>Official Rules</strong> linked in the footer of the website for more details.
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'getMoreInformation',
    question: (t) =>
      t(
        'howGetMoreInfoSketch',
        'How can I get more information about the Giveaway dates, eligibility, prizes, or other information?',
      ),
    answer: (t) => (
      <Translate t={t} i18nKey="howGetMoreInfoYWAnswer">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            The Young Washington Giveaway #1 is <strong>administered by SweepstakesPros</strong>, a leading promotion
            administrator with over 30 years of experience. For full details, visit the <strong>Official Rules</strong>{' '}
            linked in the footer of the website.
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'showtimeAvailInfo',
    question: (t) => t('showtimeAvailInfoSketch', 'When will showtimes be available for me to choose my seats?'),
    answer: (t) => (
      <Translate t={t} i18nKey="showtimeAvailInfoYoungWashtionhAnswer">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            Showtimes are typically available <strong>six weeks before the release date.</strong>
          </li>
          <li>
            For <i>Young Washington</i>, they may be available as early as <strong>May 21, 2026.</strong>
          </li>
          <li>
            These dates may fluctuate, but once showtimes are ready, we’ll notify you via:
            <ul className={'flex list-inside list-circle flex-col gap-2 pl-6'}>
              <li>Email</li>
              <li>
                Notifications on{' '}
                <a href={'https://angel.com'} target={'_blank'}>
                  angel.com
                </a>
              </li>
              <li>The Angel mobile app</li>
            </ul>
          </li>
          <li>
            Tickets can be booked at{' '}
            <a href={'https://angel.com/tickets'} target={'_blank'}>
              angel.com/tickets
            </a>{' '}
            once showtimes are available.
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'howPreOrdersWork',
    question: (t) => t('howPreOrdersWorkQuestion', 'How do pre-orders work?'),
    answer: (t) => (
      <Translate t={t} i18nKey="howPreOrdersWorkAnswerYW">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
            within 10-15 minutes after ordering).
          </li>
          <li>
            Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical run
            at participating theaters listed on{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </li>
          <li>
            Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </li>
          <li>
            Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. After that, only store
            credit will be issued. For additional terms, please visit{' '}
            <a className="underline" target="_blank" href="https://angel.com/terms">
              angel.com/terms
            </a>
            .
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'superfanInfo',
    question: (t) => t('superfanInfoSketch', 'How can I receive my pre-order tickets, entries, and superfan benefits?'),
    answer: (t) => (
      <Translate t={t} i18nKey="superfanInfoSketchAnswerAngel">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            Your <strong>pre-order ticket codes</strong> will be emailed to you upon completing your order.
          </li>
          <li>
            If you have an <strong>Angel account</strong>, you can view your ticket codes by logging into the email
            account you used to purchase tickets on{' '}
            <a href={'https://angel.com'} target={'_blank'}>
              angel.com
            </a>
            , navigating to <strong>My Account &gt; My Tickets.</strong>
          </li>
          <li>
            You can also access your tickets through the <strong>Angel mobile app</strong> under{' '}
            <strong>More &gt; Tickets.</strong>
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'refundPolicy',
    question: (t) =>
      t(
        'refundPolicyGiveawayQuestion',
        'What are the refund and expiration policies for ticket packages with sweepstakes entries?',
      ),
    answer: (t) => (
      <Translate t={t} i18nKey="refundPolicyGiveawaySketchAnswer">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            <strong>Full refunds</strong> are available <strong>within 120 days of purchase.</strong>
          </li>
          <li>
            <strong>After 120 days</strong>, you may receive <strong>store credit</strong> up until{' '}
            <strong>six weeks after the movie release date.</strong>
          </li>
          <li>
            Refund eligibility for packages with perks or sweepstakes entries:
            <ul className={'flex list-inside list-circle flex-col gap-2 pl-6'}>
              <li>
                If a <strong>perk</strong> has been used, <strong>refunds are no longer available.</strong>
              </li>
              <li>
                If a package{' '}
                <strong>includes sweepstakes entries, refunds are only possible until winners are selected</strong> or
                other specified conditions are met.
              </li>
            </ul>
          </li>
          <li>
            See the <strong>Official Rules</strong> for exact dates and conditions.
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'sharingPolicy',
    question: (t) => t('sharingPolicyQuestion', 'Can I share my pre-order tickets with others?'),
    answer: (t) => (
      <Translate t={t} i18nKey="sharingPolicyAnswer">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            Yes! Your <strong>pre-order tickets</strong> come as <strong>promo codes</strong>, which you can{' '}
            <strong>share with anyone.</strong>
          </li>
          <li>
            They can redeem these codes for showtimes when available, exclusively at{' '}
            <a href={'https://angel.com/tickets'} target={'_blank'}>
              angel.com/tickets
            </a>
            .
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'preorderOtherSketch',
    question: (t) =>
      t('preorderOtherSketchQuestionAngel', 'Can I use these pre-order tickets for another Angel movie?'),
    answer: (t) => (
      <Translate t={t} i18nKey="preorderOtherYWAnswer">
        <ul className={classNames('flex list-inside list-disc flex-col gap-2', getProjectStyle(slugs.sketch))}>
          <li>
            No. Pre-order tickets are valid only for Young Washington and can only be redeemed at{' '}
            <a href={'https://angel.com/tickets'} target={'_blank'}>
              angel.com/tickets
            </a>
            .
          </li>
        </ul>
      </Translate>
    ),
  },
  {
    key: 'contactSupport',
    question: (t) => t('contactSupportQuestion', 'How do I contact support?'),
    answer: createContactSupportAnswer(slugs.sketch),
  },
]
